import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Stack,
    Tabs,
    Tab,
    Chip,
    Divider,
    Breadcrumbs,
    Link,
    Alert,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Parser from 'html-react-parser';
import { UserContextType, useAuth } from '../../auth';
import { GroupApiError, groupService } from '../../service/GroupService';
import { GroupDetail, GroupEvent, GroupMember, GroupUpdate } from '../../types/group';
import RichText from '../../common/RichTextControl';
import GroupEventFields, { GroupEventFormValue, emptyGroupEventForm } from './GroupEventFields';
import { GroupFormDialog } from './ManageGroups';
import { formatEventRange, isBlankHtml, toDateTimeLocal, validateEventTimes } from '../../groups/groupFormat';
import AiRewriteButton from '../../common/AiRewriteButton';
import { useProofreadCheck } from '../../common/useProofreadCheck';

const Emitter = require('tiny-emitter/instance');

interface ConfirmState {
    title: string;
    message: string;
    confirmLabel: string;
    action: () => Promise<void>;
}

const rowSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    py: 1.5,
};

const ManageGroup: React.FC = () => {
    const { id } = useParams<string>();
    const { getToken, IsAdmin, cookies } = useContext<UserContextType>(useAuth);
    const isAdmin = !!IsAdmin && IsAdmin();

    const [group, setGroup] = useState<GroupDetail | null>(null);
    const [members, setMembers] = useState<GroupMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [tab, setTab] = useState(0);

    const [groupFormOpen, setGroupFormOpen] = useState(false);
    const [confirm, setConfirm] = useState<ConfirmState | null>(null);
    const [confirmBusy, setConfirmBusy] = useState(false);

    // Update dialog
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [editingUpdate, setEditingUpdate] = useState<GroupUpdate | null>(null);
    const [updateTitle, setUpdateTitle] = useState('');
    const [updateBody, setUpdateBody] = useState('');
    const [updateEditorKey, setUpdateEditorKey] = useState(0);

    // Event dialog
    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<GroupEvent | null>(null);
    const [eventForm, setEventForm] = useState<GroupEventFormValue>(emptyGroupEventForm);

    const [saving, setSaving] = useState(false);
    const updateProofread = useProofreadCheck('group-update', 'update');
    const eventProofread = useProofreadCheck('group-event', 'event description');

    const token = getToken ? getToken() : '';

    const loadGroup = async () => {
        if (!id) return;
        try {
            setGroup(await groupService.getGroup(id, token || undefined));
        } catch (err) {
            if (err instanceof GroupApiError && err.status === 404) setLoadError('Group not found.');
            else if (err instanceof GroupApiError && err.status === 403) setLoadError('You are not a leader of this group.');
            else setLoadError('Failed to load group.');
        }
    };

    const loadMembers = async () => {
        if (!id || !token) return;
        try {
            setMembers(await groupService.getMembers(id, token));
        } catch (err) {
            if (err instanceof GroupApiError && err.status === 403) setLoadError('You are not a leader of this group.');
            else Emitter.emit('notify', 'Failed to load members.', 'error');
        }
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setLoadError('');
            await Promise.all([loadGroup(), loadMembers()]);
            setLoading(false);
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, cookies]);

    const pending = useMemo(() => members.filter((m) => m.status === 'Pending'), [members]);
    const approved = useMemo(
        () => members
            .filter((m) => m.status === 'Approved')
            .sort((a, b) => Number(b.isAdmin) - Number(a.isAdmin) || a.lastName.localeCompare(b.lastName)),
        [members]
    );
    const declined = useMemo(() => members.filter((m) => m.status === 'Declined'), [members]);

    const updates = useMemo(
        () => [...(group?.updates ?? [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
        [group]
    );
    const events = useMemo(
        () => [...(group?.events ?? [])].sort((a, b) => new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime()),
        [group]
    );

    // ---- Members ----
    const handleDecide = async (member: GroupMember, approve: boolean) => {
        if (!id) return;
        try {
            await groupService.decideMember(id, member.userId, approve, token);
            Emitter.emit('notify', `${member.firstName} ${member.lastName} ${approve ? 'approved' : 'declined'}.`, 'success');
            await Promise.all([loadMembers(), loadGroup()]);
        } catch (err) {
            Emitter.emit('notify', 'Failed to update request.', 'error');
        }
    };

    const handleToggleLeader = async (member: GroupMember, makeLeader: boolean) => {
        if (!id) return;
        try {
            await groupService.setMemberAdmin(id, member.userId, makeLeader, token);
            Emitter.emit('notify', `${member.firstName} ${member.lastName} is ${makeLeader ? 'now a leader' : 'no longer a leader'}.`, 'success');
            await loadMembers();
        } catch (err) {
            Emitter.emit('notify', 'Failed to update leader.', 'error');
        }
    };

    const confirmRemoveMember = (member: GroupMember) => {
        setConfirm({
            title: 'Remove member?',
            message: `Remove ${member.firstName} ${member.lastName} from ${group?.name}? They will need to request to join again.`,
            confirmLabel: 'Remove',
            action: async () => {
                await groupService.removeMember(id!, member.userId, token);
                Emitter.emit('notify', 'Member removed.', 'success');
                await Promise.all([loadMembers(), loadGroup()]);
            },
        });
    };

    // ---- Updates ----
    const openUpdateDialog = (update: GroupUpdate | null) => {
        setEditingUpdate(update);
        setUpdateTitle(update?.title ?? '');
        setUpdateBody(update?.body ?? '');
        setUpdateEditorKey((k) => k + 1);
        setUpdateDialogOpen(true);
    };

    const handleSaveUpdate = async () => {
        if (!id) return;
        if (!updateTitle.trim() || isBlankHtml(updateBody)) {
            Emitter.emit('notify', 'Please fill in both a title and content for the update.', 'error');
            return;
        }
        await updateProofread.checkThenSave(updateBody, saveUpdate);
    };

    const saveUpdate = async (body: string) => {
        if (!id) return;
        setUpdateBody(body);
        setSaving(true);
        try {
            const request = { title: updateTitle.trim(), body };
            if (editingUpdate) await groupService.editUpdate(id, editingUpdate.id, request, token);
            else await groupService.createUpdate(id, request, token);
            Emitter.emit('notify', editingUpdate ? 'Update saved.' : 'Update posted.', 'success');
            setUpdateDialogOpen(false);
            await loadGroup();
        } catch (err) {
            Emitter.emit('notify', 'Failed to save update.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteUpdate = (update: GroupUpdate) => {
        setConfirm({
            title: 'Delete update?',
            message: `Delete "${update.title}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            action: async () => {
                await groupService.deleteUpdate(id!, update.id, token);
                Emitter.emit('notify', 'Update deleted.', 'success');
                await loadGroup();
            },
        });
    };

    // ---- Events ----
    const openEventDialog = (event: GroupEvent | null) => {
        setEditingEvent(event);
        setEventForm(event
            ? {
                title: event.title,
                description: event.description ?? '',
                startDateTime: toDateTimeLocal(event.startDateTime),
                endDateTime: toDateTimeLocal(event.endDateTime),
                location: event.location ?? '',
            }
            : emptyGroupEventForm);
        setEventDialogOpen(true);
    };

    const handleSaveEvent = async () => {
        if (!id) return;
        const error = validateEventTimes(eventForm);
        if (error) {
            Emitter.emit('notify', error, 'error');
            return;
        }
        await eventProofread.checkThenSave(eventForm.description, saveEvent);
    };

    const saveEvent = async (description: string) => {
        if (!id) return;
        const { title, startDateTime, endDateTime } = eventForm;
        setEventForm((prev) => ({ ...prev, description }));
        setSaving(true);
        try {
            const request = {
                title: title.trim(),
                description: description.trim() || null,
                startDateTime,
                endDateTime,
                location: eventForm.location.trim() || null,
            };
            if (editingEvent) await groupService.editEvent(id, editingEvent.id, request, token);
            else await groupService.createEvent(id, request, token);
            Emitter.emit('notify', editingEvent ? 'Event saved.' : 'Event created.', 'success');
            setEventDialogOpen(false);
            await loadGroup();
        } catch (err) {
            Emitter.emit('notify', 'Failed to save event.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteEvent = (event: GroupEvent) => {
        setConfirm({
            title: 'Delete event?',
            message: `Delete "${event.title}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            action: async () => {
                await groupService.deleteEvent(id!, event.id, token);
                Emitter.emit('notify', 'Event deleted.', 'success');
                await loadGroup();
            },
        });
    };

    const runConfirm = async () => {
        if (!confirm) return;
        setConfirmBusy(true);
        try {
            await confirm.action();
            setConfirm(null);
        } catch (err) {
            Emitter.emit('notify', 'Something went wrong. Please try again.', 'error');
        } finally {
            setConfirmBusy(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, pt: 10, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError || !group) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 }, pt: 10 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{loadError || 'Group not found.'}</Alert>
                <Button component={RouterLink} to="/admin/groups" variant="outlined">Back to Manage Groups</Button>
            </Box>
        );
    }

    const renderMember = (member: GroupMember, actions: React.ReactNode) => (
        <Box key={member.userId} sx={rowSx}>
            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>{member.firstName} {member.lastName}</Typography>
                    {member.isAdmin && <Chip label="Leader" color="primary" size="small" />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {member.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {member.status === 'Pending'
                        ? `Requested ${new Date(member.requestedAt).toLocaleDateString()}`
                        : member.decidedAt
                            ? `${member.status} ${new Date(member.decidedAt).toLocaleDateString()}`
                            : ''}
                </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {actions}
            </Stack>
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, pt: 10 }}>
            <Breadcrumbs sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/admin/groups" underline="hover">Manage Groups</Link>
                <Typography color="text.primary">{group.name}</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h4">{group.name}</Typography>
                    <Chip
                        label={group.isActive ? 'Active' : 'Inactive'}
                        color={group.isActive ? 'success' : 'default'}
                        size="small"
                    />
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        component={RouterLink}
                        to={`/groups/${group.id}`}
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                    >
                        View
                    </Button>
                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setGroupFormOpen(true)}>
                        Edit
                    </Button>
                </Stack>
            </Box>
            {group.meetingInfo && (
                <Typography color="text.secondary" sx={{ mb: 2 }}>{group.meetingInfo}</Typography>
            )}

            <Paper>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label={`Members (${approved.length})${pending.length ? ` · ${pending.length} pending` : ''}`} />
                    <Tab label={`Updates (${updates.length})`} />
                    <Tab label={`Events (${events.length})`} />
                </Tabs>

                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Members */}
                    {tab === 0 && (
                        <Box>
                            <Typography variant="h6">Pending requests</Typography>
                            {pending.length === 0 ? (
                                <Typography color="text.secondary" sx={{ py: 1.5 }}>No pending requests.</Typography>
                            ) : (
                                pending.map((m, i) => (
                                    <React.Fragment key={m.userId}>
                                        {i > 0 && <Divider />}
                                        {renderMember(m, (
                                            <>
                                                <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => handleDecide(m, true)}>
                                                    Approve
                                                </Button>
                                                <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />} onClick={() => handleDecide(m, false)}>
                                                    Decline
                                                </Button>
                                            </>
                                        ))}
                                    </React.Fragment>
                                ))
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="h6">Members</Typography>
                            {approved.length === 0 ? (
                                <Typography color="text.secondary" sx={{ py: 1.5 }}>No members yet.</Typography>
                            ) : (
                                approved.map((m, i) => (
                                    <React.Fragment key={m.userId}>
                                        {i > 0 && <Divider />}
                                        {renderMember(m, (
                                            <>
                                                {isAdmin && (
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                size="small"
                                                                checked={m.isAdmin}
                                                                onChange={(e) => handleToggleLeader(m, e.target.checked)}
                                                            />
                                                        }
                                                        label="Leader"
                                                    />
                                                )}
                                                <Button size="small" color="error" onClick={() => confirmRemoveMember(m)}>
                                                    Remove
                                                </Button>
                                            </>
                                        ))}
                                    </React.Fragment>
                                ))
                            )}

                            {declined.length > 0 && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6">Declined</Typography>
                                    {declined.map((m, i) => (
                                        <React.Fragment key={m.userId}>
                                            {i > 0 && <Divider />}
                                            {renderMember(m, (
                                                <Button size="small" onClick={() => handleDecide(m, true)}>Approve</Button>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </>
                            )}
                        </Box>
                    )}

                    {/* Updates */}
                    {tab === 1 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openUpdateDialog(null)}>
                                    New Update
                                </Button>
                            </Box>
                            {updates.length === 0 && (
                                <Typography color="text.secondary" sx={{ py: 1.5 }}>No updates yet.</Typography>
                            )}
                            {updates.map((update, i) => (
                                <React.Fragment key={update.id}>
                                    {i > 0 && <Divider />}
                                    <Box sx={{ py: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 600 }}>{update.title}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(update.createdAt).toLocaleString()}
                                                    {update.createdBy ? ` · ${update.createdBy}` : ''}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row">
                                                <Tooltip title="Edit">
                                                    <IconButton onClick={() => openUpdateDialog(update)}><EditIcon /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton onClick={() => confirmDeleteUpdate(update)}><DeleteIcon /></IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Box>
                                        <Box sx={{ mt: 1, '& p': { m: 0, mb: 1 }, '& img': { maxWidth: '100%' } }}>
                                            {Parser(update.body || '')}
                                        </Box>
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    )}

                    {/* Events */}
                    {tab === 2 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEventDialog(null)}>
                                    New Event
                                </Button>
                            </Box>
                            {events.length === 0 && (
                                <Typography color="text.secondary" sx={{ py: 1.5 }}>No events yet.</Typography>
                            )}
                            {events.map((event, i) => (
                                <React.Fragment key={event.id}>
                                    {i > 0 && <Divider />}
                                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontWeight: 600 }}>{event.title}</Typography>
                                            <Typography variant="body2" color="text.secondary">{formatEventRange(event)}</Typography>
                                            {event.location && (
                                                <Typography variant="body2" color="text.secondary">{event.location}</Typography>
                                            )}
                                            {event.description && (
                                                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{event.description}</Typography>
                                            )}
                                        </Box>
                                        <Stack direction="row">
                                            <Tooltip title="Edit">
                                                <IconButton onClick={() => openEventDialog(event)}><EditIcon /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton onClick={() => confirmDeleteEvent(event)}><DeleteIcon /></IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                </Box>
            </Paper>

            <GroupFormDialog
                open={groupFormOpen}
                group={group}
                onClose={() => setGroupFormOpen(false)}
                onSaved={loadGroup}
            />

            {/* Update dialog */}
            <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{editingUpdate ? 'Edit Update' : 'New Update'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Title"
                            value={updateTitle}
                            onChange={(e) => setUpdateTitle(e.target.value)}
                            required
                            fullWidth
                        />
                        <Box sx={{ border: '1px solid #ccc', borderRadius: 1, minHeight: '200px' }}>
                            <RichText
                                key={updateEditorKey}
                                text={updateBody}
                                onChange={(html: string) => setUpdateBody(html)}
                            />
                        </Box>
                        <Box>
                            <AiRewriteButton
                                content={updateBody}
                                contentType="group-update"
                                isHtml
                                disabled={saving}
                                onAccept={setUpdateBody}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdateDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSaveUpdate} variant="contained" disabled={saving || updateProofread.isChecking}>
                        {updateProofread.isChecking ? 'Checking...' : editingUpdate ? 'Save' : 'Post'}
                    </Button>
                </DialogActions>
                {updateProofread.proofreadDialog}
            </Dialog>

            {/* Event dialog */}
            <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editingEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
                <DialogContent>
                    <GroupEventFields value={eventForm} onChange={setEventForm} disabled={saving} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEventDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSaveEvent} variant="contained" disabled={saving || eventProofread.isChecking}>
                        {eventProofread.isChecking ? 'Checking...' : editingEvent ? 'Save' : 'Create'}
                    </Button>
                </DialogActions>
                {eventProofread.proofreadDialog}
            </Dialog>

            {/* Confirm dialog (remove member / delete update / delete event) */}
            <Dialog open={!!confirm} onClose={() => !confirmBusy && setConfirm(null)}>
                <DialogTitle>{confirm?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirm?.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={confirmBusy}>Cancel</Button>
                    <Button onClick={runConfirm} color="error" variant="contained" disabled={confirmBusy}>
                        {confirm?.confirmLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManageGroup;
